// Connector provider SVG icons - copied from dataProtect reactflow/assets/
import amazonRdsIcon from '@/assets/icons/connectors/amazon-rds-icon.svg';
import amazonRedshiftIcon from '@/assets/icons/connectors/amazon-redshift-icon.svg';
import anthologyIcon from '@/assets/icons/connectors/anthology-icon.svg';
import awsElasticSearchIcon from '@/assets/icons/connectors/aws-elastic-search-icon.svg';
import awsS3Icon from '@/assets/icons/connectors/aws-s3-icon.svg';
import awsSesIcon from '@/assets/icons/connectors/aws-ses-icon.svg';
import blackboardIcon from '@/assets/icons/connectors/blackboard-icon.svg';
import brightspaceValenceIcon from '@/assets/icons/connectors/brightspace-valence-icon.svg';
import canvasIcon from '@/assets/icons/connectors/canvas-icon.svg';
import colleagueIcon from '@/assets/icons/connectors/colleague-icon.svg';
import docusignIcon from '@/assets/icons/connectors/docusign-icon.svg';
import edfiIcon from '@/assets/icons/connectors/edfi-icon.svg';
import edreadyIcon from '@/assets/icons/connectors/edready-icon.svg';
import element451Icon from '@/assets/icons/connectors/element451-icon.svg';
import ethosIcon from '@/assets/icons/connectors/ethos-icon.svg';
import googleBigQueryIcon from '@/assets/icons/connectors/google-big-query-icon.svg';
import googleSheetsIcon from '@/assets/icons/connectors/google-sheets-icon.svg';
import graphqlIcon from '@/assets/icons/connectors/graphql-icon.svg';
import httpIcon from '@/assets/icons/connectors/http-icon-icon.svg';
import httpXmlIcon from '@/assets/icons/connectors/http-xml-icon.svg';
import infosilemIcon from '@/assets/icons/connectors/infosilem-icon.svg';
import jdbcIcon from '@/assets/icons/connectors/jdbc-icon.svg';
import jsonIcon from '@/assets/icons/connectors/json-icon.svg';
import leadSquaredIcon from '@/assets/icons/connectors/lead-squared-icon.svg';
import learningStreamIcon from '@/assets/icons/connectors/learning-stream-icon.svg';
import lingkIcon from '@/assets/icons/connectors/lingk-icon.svg';
import localFileIcon from '@/assets/icons/connectors/local-file-icon.svg';
import microsoftCosmosDbIcon from '@/assets/icons/connectors/microsoft-cosmos-db-icon.svg';
import moodleIcon from '@/assets/icons/connectors/moodle-icon.svg';
import odataIcon from '@/assets/icons/connectors/odata-icon.svg';
import paycomIcon from '@/assets/icons/connectors/paycom-icon.svg';
import peopleSoftIcon from '@/assets/icons/connectors/people-soft-icon.svg';
import salesforceIcon from '@/assets/icons/connectors/salesforce-icon.svg';
import sftpIcon from '@/assets/icons/connectors/sftp-icon.svg';
import sqlServerIcon from '@/assets/icons/connectors/sql-server-icon.svg';
import zoomIcon from '@/assets/icons/connectors/zoom-icon.svg';

import { Storage as StorageIcon } from '@mui/icons-material';

/**
 * Map of provider type (lowercase) to SVG icon path.
 * Keys match the `type` or `credentialType` field from IntegrationProvider,
 * lowercased for case-insensitive lookup.
 */
const PROVIDER_ICON_MAP: Record<string, string> = {
  // Amazon / AWS
  amazonrds: amazonRdsIcon,
  'amazon rds': amazonRdsIcon,
  redshift: amazonRedshiftIcon,
  amazonredshift: amazonRedshiftIcon,
  awselasticsearch: awsElasticSearchIcon,
  elasticsearch: awsElasticSearchIcon,
  amazons3: awsS3Icon,
  s3: awsS3Icon,
  amazonses: awsSesIcon,
  ses: awsSesIcon,

  // Education
  anthology: anthologyIcon,
  blackboard: blackboardIcon,
  brightspacevalenceapi: brightspaceValenceIcon,
  brightspace: brightspaceValenceIcon,
  canvas: canvasIcon,
  canvasoauth2: canvasIcon,
  colleague: colleagueIcon,
  edfi: edfiIcon,
  edready: edreadyIcon,
  element451: element451Icon,
  ethos: ethosIcon,

  // DocuSign
  docusign: docusignIcon,

  // Google
  googlebigquery: googleBigQueryIcon,
  googlespreadsheet: googleSheetsIcon,
  googlesheets: googleSheetsIcon,

  // GraphQL
  graphql: graphqlIcon,

  // HTTP
  http: httpIcon,
  httpv2: httpIcon,
  httpxml: httpXmlIcon,

  // Infosilem
  infosilem: infosilemIcon,

  // Database
  jdbc: jdbcIcon,
  sqlserver: sqlServerIcon,

  // JSON
  json: jsonIcon,

  // LeadSquared
  leadsquared: leadSquaredIcon,

  // LearningStream
  learningstream: learningStreamIcon,

  // Lingk
  lingk: lingkIcon,
  lingkadapter: lingkIcon,

  // LocalFile / File Storage
  localfile: localFileIcon,
  flatfile: localFileIcon,
  filestorage: localFileIcon,
  'file storage': localFileIcon,

  // Microsoft
  microsoftcosmosdb: microsoftCosmosDbIcon,
  cosmosdb: microsoftCosmosDbIcon,

  // Moodle
  moodle: moodleIcon,

  // OData
  odata: odataIcon,

  // Paycom
  paycom: paycomIcon,

  // PeopleSoft
  peoplesoft: peopleSoftIcon,

  // Salesforce
  salesforce: salesforceIcon,

  // SFTP
  sftp: sftpIcon,

  // Zoom
  zoom: zoomIcon,
};

/**
 * Get the appropriate icon for a connector based on its type.
 * Uses the actual provider SVG icons from the legacy dataProtect app.
 * Falls back to a generic MUI icon if no match is found.
 */
export const getConnectorIcon = (
  providerLabel: string,
  credentialType: string
): React.ReactElement => {
  // Try matching by credentialType first, then providerLabel
  const typeKey = credentialType.toLowerCase().replace(/[\s-]/g, '');
  const labelKey = providerLabel.toLowerCase().replace(/[\s-]/g, '');

  const iconSrc = PROVIDER_ICON_MAP[typeKey] || PROVIDER_ICON_MAP[labelKey];

  if (iconSrc) {
    return <img src={iconSrc} alt={providerLabel} style={{ width: 20, height: 20 }} />;
  }

  // Fallback: generic storage icon
  return <StorageIcon sx={{ color: '#666', fontSize: 20 }} />;
};

/**
 * Get SVG icon src URL for a provider type string.
 * Returns undefined if no icon found.
 */
export const getConnectorIconSrc = (providerType: string): string | undefined => {
  const key = providerType.toLowerCase().replace(/[\s-]/g, '');
  return PROVIDER_ICON_MAP[key];
};
